'use client';

import React, { useState } from 'react';
import { Modal, IconButton, Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css/bundle';
import ArrowBackIosNewRounded from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowForwardIosRounded from '@mui/icons-material/ArrowForwardIosRounded';
import OptimizedImage from './OptimizedImage';

interface FullScreenGalleryViewerProps {
    images: string[];
    initialIndex?: number;
    onClose: () => void;
    title?: string;
    altTexts?: Record<string, string>;
}

const FullScreenGalleryViewer: React.FC<FullScreenGalleryViewerProps> = ({
    images,
    initialIndex = 0,
    onClose,
    title = '',
    altTexts,
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    return (
        <Modal
            open={true}
            onClose={onClose}
            className="flex items-center justify-center"
        >
            <div className="relative h-screen w-screen bg-black bg-opacity-95">
                {/* Top bar with close button */}
                <div className="absolute top-6 sm:top-2 md:top-0 left-0 right-0 pt-4 sm:pt-2 md:pt-0 h-20 sm:h-16 md:h-16 bg-gradient-to-b from-black to-transparent z-50 flex items-center justify-between px-4">
                    <Button
                        onClick={onClose}
                        variant="text"
                        startIcon={<CloseIcon />}
                        sx={{
                            color: 'white',
                            textTransform: 'none',
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                            padding: { xs: '6px 12px', sm: '8px 16px' },
                            borderRadius: '20px',
                            minWidth: { xs: 'auto', sm: '80px' },
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)'
                            }
                        }}
                    >
                        <span className="hidden sm:inline">Close</span>
                    </Button>

                    <Typography
                        sx={{
                            fontSize: { xs: '0.9rem', sm: '1.1rem' },
                            color: 'white',
                            flexGrow: 1,
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: { xs: '150px', sm: '300px', md: 'none' },
                            mx: 'auto'
                        }}
                    >
                        {title}
                    </Typography>

                    <div className="w-10 sm:w-20"></div> {/* Spacer for balance */}
                </div>

                <div className="absolute inset-0 flex items-center justify-center">
                    <Swiper
                        modules={[Navigation]}
                        spaceBetween={0}
                        slidesPerView={1}
                        initialSlide={initialIndex}
                        loop={true}
                        navigation={{
                            prevEl: '.swiper-button-prev',
                            nextEl: '.swiper-button-next',
                        }}
                        onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex)}
                        className="h-full w-full"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                        }}
                    >
                        {images.map((image, index) => (
                            <SwiperSlide key={index}>
                                <div className="flex items-center justify-center h-full p-4">
                                    <OptimizedImage
                                        src={image}
                                        alt={altTexts?.[image] || `Image ${index + 1}`}
                                        fill
                                        className="!relative max-h-[80vh] max-w-[90vw] w-auto h-auto object-contain"
                                        quality={100}
                                        priority={index === initialIndex || Math.abs(index - currentIndex) <= 1}
                                        sizes="(max-width: 640px) 95vw, 90vw"
                                        placeholder="blur"
                                        generateBlurPlaceholder={true}
                                        style={{
                                            maxHeight: 'calc(100vh - 120px)', /* Account for top/bottom bars and padding */
                                            objectFit: 'contain',
                                            margin: '0 auto',
                                        }}
                                    />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    <div className="absolute inset-0">
                        <div className="relative w-full h-full">
                            <div className="absolute top-1/2 transform -translate-y-1/2 left-2 sm:left-4 z-50">
                                <IconButton
                                    className="swiper-button-prev backdrop-blur-sm"
                                    sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                        color: 'white',
                                        padding: { xs: '8px', sm: '12px' },
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                            transform: 'scale(1.1)',
                                        },
                                    }}
                                >
                                    <ArrowBackIosNewRounded sx={{ fontSize: { xs: 20, sm: 28 } }} />
                                </IconButton>
                            </div>
                            <div className="absolute top-1/2 transform -translate-y-1/2 right-2 sm:right-4 z-50">
                                <IconButton
                                    className="swiper-button-next backdrop-blur-sm"
                                    sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                        color: 'white',
                                        padding: { xs: '8px', sm: '12px' },
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                            transform: 'scale(1.1)',
                                        },
                                    }}
                                >
                                    <ArrowForwardIosRounded sx={{ fontSize: { xs: 20, sm: 28 } }} />
                                </IconButton>
                            </div>
                        </div>
                    </div>

                </div>

                <div className="absolute bottom-4 left-0 right-0 flex justify-center z-50">
                    <span className="bg-black bg-opacity-70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
                        {currentIndex + 1} / {images.length}
                    </span>
                </div>
            </div>
        </Modal>
    );
};

export default FullScreenGalleryViewer;
